import React, { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { AlertCircle, X } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { useAtualizarPerfil, useCriarPerfil } from '../hooks';
import type { Role } from '../api';

interface Props {
  open: boolean;
  /** Perfil em edição; ausente cria um novo. */
  perfil?: Role | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}

export const PerfilFormModal: React.FC<Props> = ({ open, perfil, onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const criar = useCriarPerfil();
  const atualizar = useAtualizarPerfil();
  const isEdit = Boolean(perfil);
  const saving = criar.isPending || atualizar.isPending;

  useEffect(() => {
    if (!open) return;
    setName(perfil?.name ?? '');
    setDescription(perfil?.description ?? '');
    setError(null);
  }, [open, perfil]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      if (isEdit && perfil) {
        await atualizar.mutateAsync({ id: perfil.id, payload: { name, description } });
        onSaved(`Perfil "${name}" atualizado.`);
      } else {
        await criar.mutateAsync({ name, description });
        onSaved(`Perfil "${name}" criado. Agora atribua as funcionalidades.`);
      }
      onClose();
    } catch (err) {
      // Mantém o formulário preenchido: em conflito de nome, o usuário só
      // precisa ajustar o campo, não digitar tudo de novo.
      const response = (err as { response?: { data?: { message?: string | string[] } } }).response;
      const message = response?.data?.message;
      setError(Array.isArray(message) ? message.join(', ') : message ?? 'Não foi possível salvar o perfil.');
    }
  }

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-lg rounded-xl bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              {isEdit ? 'Editar perfil' : 'Novo perfil'}
            </Dialog.Title>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
            {error && (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <TextInput
              label="Nome do perfil"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Descrição</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="O que este perfil faz no sistema"
              />
            </div>

            {isEdit && perfil && (
              <p className="text-xs text-gray-500">
                Identificador: <code className="rounded bg-gray-100 px-1 py-0.5">{perfil.slug}</code>
                {' '}— não pode ser alterado após a criação.
              </p>
            )}
            {!isEdit && (
              <p className="text-xs text-gray-500">
                O perfil é criado sem nenhuma permissão. Você atribui as funcionalidades na grade ao lado.
              </p>
            )}

            <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving || !name.trim()}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? 'Salvando…' : isEdit ? 'Salvar' : 'Criar perfil'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
